import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { Form, Col, Row } from "react-bootstrap";
import PropTypes from "prop-types";
import { v4 as uuidv4 } from "uuid";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";

import ConditionalButton from "../../../layout/conditional-button";
import ErrorModal from "../../../layout/modal";
import DeleteModal from "../../../layout/modal";

import { isGuid } from "../../../utils/string";
import { withNavigation } from "../../../utils/wrapper";

import { send as pageInfo, loading } from "../../../actions/page";
import {
  get as getPaySchedule,
  save as savePaySchedule,
  destroy as destroyPaySchedule,
} from "../../../actions/pay.schedules";

const FREQUENCY_OPTIONS = ["weekly", "biweekly", "semimonthly", "monthly", "annually"];

const normalizeDateToISO = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const toDateOrNull = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const mapPropsToData = (props) => ({
  uuid: props?.uuid || null,
  name: props?.name || "",
  frequency: props?.frequency || FREQUENCY_OPTIONS[0],
  anchorDate: toDateOrNull(props?.anchorDate),
});

const buildAutoLabel = (d) => {
  const date = toDateOrNull(d?.anchorDate);
  if (!date || !d?.frequency) return d?.name || "";

  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${d.frequency} starting on ${y}-${m}-${day}`;
};

function Detail(props) {
  const {
    auth, // kept in case permission gating is added later
    params,
    navigate,
    onChange: onExternalChange,
    pageInfo,
    loading,
    getPaySchedule,
    savePaySchedule,
    destroyPaySchedule,
  } = props;

  const uuid = params?.uuid;

  const didMountRef = useRef(false);
  const initialDataRef = useRef(mapPropsToData(props));
  const onExternalChangeRef = useRef(onExternalChange);

  const [data, setData] = useState(() => initialDataRef.current);
  const [modal, setModal] = useState({
    error: { show: false, title: "", body: "" },
    delete: { show: false, title: "", body: "" },
  });

  // Keep latest callback reference without retriggering data effect
  useEffect(() => {
    onExternalChangeRef.current = onExternalChange;
  }, [onExternalChange]);

  // Notify parent only when data actually changes (not when callback identity changes)
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (onExternalChangeRef.current) {
      onExternalChangeRef.current({ data });
    }
  }, [data]);

  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const frequencyOptions = useMemo(
    () =>
      FREQUENCY_OPTIONS.map((s) => ({
        value: s,
        label: s.charAt(0).toUpperCase() + s.slice(1),
      })),
    []
  );

  const selectedFrequencyOption = useMemo(() => {
    const value = data?.frequency || FREQUENCY_OPTIONS[0];
    return frequencyOptions.find((o) => o.value === value) || frequencyOptions[0];
  }, [data?.frequency, frequencyOptions]);

  const setField = useCallback((name, value, { maybeAutolabel = false } = {}) => {
    setData((prev) => {
      const prevValue = prev?.[name];

      const same =
        prevValue === value ||
        (prevValue instanceof Date &&
          value instanceof Date &&
          prevValue.getTime() === value.getTime());

      if (same && !maybeAutolabel) return prev;

      const next = { ...prev, [name]: value };

      if (maybeAutolabel && !String(prev?.name || "").trim()) {
        const auto = buildAutoLabel(next);
        if (auto) next.name = auto;
      }

      return next;
    });
  }, []);

  const showError = useCallback((title, body) => {
    setModal((prev) => ({
      ...prev,
      error: { show: true, title, body },
    }));
  }, []);

  const validate = useCallback((d) => {
    const errors = [];

    if (!d?.name || !String(d.name).trim()) {
      errors.push("Label is required.");
    }

    if (!FREQUENCY_OPTIONS.includes(d?.frequency)) {
      errors.push("Frequency is invalid.");
    }

    if (!toDateOrNull(d?.anchorDate)) {
      errors.push("Anchor date is required.");
    }

    return errors;
  }, []);

  const loadData = useCallback(async () => {
    loading(true);

    try {
      if (isGuid(uuid)) {
        const item = await getPaySchedule(uuid);

        if (!mountedRef.current) return;

        if (item) {
          setData((prev) => {
            const next = {
              ...item,
              anchorDate: toDateOrNull(item?.anchorDate),
            };

            const prevAnchor = toDateOrNull(prev?.anchorDate);
            const nextAnchor = toDateOrNull(next?.anchorDate);

            const equal =
              prev?.uuid === next?.uuid &&
              prev?.name === next?.name &&
              prev?.frequency === next?.frequency &&
              ((prevAnchor && nextAnchor && prevAnchor.getTime() === nextAnchor.getTime()) ||
                (!prevAnchor && !nextAnchor));

            return equal ? prev : next;
          });

          await pageInfo({
            title: `Pay Schedule (${item?.name || ""})`,
            breadcrumb: `${item?.name || ""}`,
          });
        } else {
          await pageInfo({ title: "Pay Schedule", breadcrumb: "[New Pay Schedule]" });
        }
      } else {
        // New record: restore stable initial seed only
        setData((prev) => {
          const next = initialDataRef.current;

          const prevAnchor = toDateOrNull(prev?.anchorDate);
          const nextAnchor = toDateOrNull(next?.anchorDate);

          const equal =
            prev?.uuid === next?.uuid &&
            prev?.name === next?.name &&
            prev?.frequency === next?.frequency &&
            ((prevAnchor && nextAnchor && prevAnchor.getTime() === nextAnchor.getTime()) ||
              (!prevAnchor && !nextAnchor));

          return equal ? prev : next;
        });

        await pageInfo({ title: "Pay Schedule", breadcrumb: "[New Pay Schedule]" });
      }
    } catch (err) {
      if (mountedRef.current) {
        showError("Load Error", err?.message || "Unable to load pay schedule.");
      }
    } finally {
      if (mountedRef.current) loading(false);
    }
  }, [loading, uuid, getPaySchedule, pageInfo, showError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const onFrequencyChange = (opt) => {
    const value = opt?.value ?? FREQUENCY_OPTIONS[0];
    setField("frequency", value, { maybeAutolabel: true });
  };

  const onSaveClick = async () => {
    const errors = validate(data);
    if (errors.length) {
      showError("Validation Error", errors.join(" "));
      return;
    }

    loading(true);
    try {
      const currentUuid = isGuid(uuid) ? uuid : uuidv4();

      const payload = {
        ...data,
        uuid: currentUuid,
        frequency: data?.frequency || FREQUENCY_OPTIONS[0],
        anchorDate: normalizeDateToISO(data?.anchorDate),
      };

      const saved = await savePaySchedule(payload);
      const id = saved?.uuid ?? currentUuid;

      navigate(`/dashboard/accounting/payschedules/${id}`);
    } catch (err) {
      showError("Save Error", err?.message || "Unable to save pay schedule.");
    } finally {
      loading(false);
    }
  };

  const onDeleteClick = () => {
    const caption = data?.name?.trim() || "this pay schedule";
    setModal((prev) => ({
      ...prev,
      delete: {
        show: true,
        title: "Delete pay schedule",
        body: `This action will permanently delete ${caption}.`,
      },
    }));
  };

  const onDeleteOkModal = async () => {
    try {
      const deleteId = data?.uuid || uuid;
      if (!isGuid(deleteId)) {
        showError("Delete Error", "Invalid pay schedule identifier.");
        return;
      }

      await destroyPaySchedule(deleteId);

      setModal((prev) => ({
        ...prev,
        delete: { ...prev.delete, show: false },
      }));

      navigate("/dashboard/accounting/payschedules");
    } catch (err) {
      showError("Delete Error", err?.message || "Unable to delete pay schedule.");
    }
  };

  const onDeleteHideModal = () => {
    setModal((prev) => ({
      ...prev,
      delete: { ...prev.delete, show: false },
    }));
  };

  const onErrorHideModal = () => {
    setModal((prev) => ({
      ...prev,
      error: { ...prev.error, show: false },
    }));
  };

  return (
    <>
      <Col md="8" className="float-right text-end">
        <ConditionalButton
          enabledVariant="outline-primary"
          tooltip="Save Pay Schedule"
          display={true}
          enabled={true}
          onEnabledClick={onSaveClick}
          icon="save"
        >
          Save
        </ConditionalButton>

        <ConditionalButton
          enabledVariant="outline-primary"
          tooltip="Delete Pay Schedule"
          display={true}
          enabled={isGuid(uuid)}
          onEnabledClick={onDeleteClick}
          icon="bin"
        >
          Delete
        </ConditionalButton>
      </Col>

      <Col xs="12">
        <Row>
          <Col xs="12" md="8" xl="4">
            <div className="mb-3 top-label">
              <Form.Control
                type="text"
                name="name"
                value={data?.name || ""}
                onChange={(e) => setField(e.target.name, e.target.value)}
                placeholder="e.g., Biweekly starting on 2026-01-01"
              />
              <Form.Label>LABEL</Form.Label>
            </div>
          </Col>

          <Col xs="12" md="4">
            <div className="mb-3 top-label">
              <Select
                inputId="frequency"
                classNamePrefix="react-select"
                isClearable={false}
                options={frequencyOptions}
                value={selectedFrequencyOption}
                onChange={onFrequencyChange}
              />
              <Form.Label>FREQUENCY</Form.Label>
            </div>
          </Col>

          <Col xs="12" md="4">
            <div className="mb-3 top-label">
              <DatePicker
                id="anchorDate"
                name="anchorDate"
                className="form-control"
                selected={toDateOrNull(data?.anchorDate)}
                onChange={(date) => setField("anchorDate", date, { maybeAutolabel: true })}
                dateFormat="yyyy-MM-dd"
                placeholderText="Select anchor date"
              />
              <Form.Label>ANCHOR DATE</Form.Label>
            </div>
          </Col>
        </Row>
      </Col>

      <ErrorModal
        show={modal.error.show}
        title={modal.error.title}
        body={modal.error.body}
        buttons={[
          {
            default: true,
            caption: "ok",
            onEnabledClick: onErrorHideModal,
            variant: "secondary",
            display: true,
            enabled: true,
          },
        ]}
      />

      <DeleteModal
        show={modal.delete.show}
        title={modal.delete.title}
        body={modal.delete.body}
        buttons={[
          {
            caption: "ok",
            onEnabledClick: onDeleteOkModal,
            variant: "primary",
            display: true,
            enabled: true,
          },
          {
            default: true,
            caption: "cancel",
            onEnabledClick: onDeleteHideModal,
            variant: "secondary",
            display: true,
            enabled: true,
          },
        ]}
      />
    </>
  );
}

Detail.propTypes = {
  auth: PropTypes.object,
  params: PropTypes.object,
  navigate: PropTypes.func,
  onChange: PropTypes.func,
  pageInfo: PropTypes.func.isRequired,
  loading: PropTypes.func.isRequired,
  getPaySchedule: PropTypes.func.isRequired,
  savePaySchedule: PropTypes.func.isRequired,
  destroyPaySchedule: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default withNavigation(
  connect(mapStateToProps, {
    pageInfo,
    loading,
    getPaySchedule,
    savePaySchedule,
    destroyPaySchedule,
  })(Detail)
);
